import { timestampDTOSchema } from '@tempo-io/common-dto';
import { z } from 'zod';
import { findingGroups, retiredReasons } from './finding-dto';
import { trackedItemStatusSchema } from './tracked-item-status';

export {
  type TrackedItemStatus,
  trackedItemStatuses,
  trackedItemStatusSchema,
} from './tracked-item-status';

export const humanSettableTrackedItemStatuses = [
  'pinned',
  'assigned',
  'in_motion',
  'unassigned',
  'completed',
  'dismissed',
] as const;
export type HumanSettableTrackedItemStatus = (typeof humanSettableTrackedItemStatuses)[number];
export const humanSettableTrackedItemStatusSchema = z.enum(humanSettableTrackedItemStatuses);

/**
 * Verification state of a tracked item's grounding snapshot. `unverified` until the deterministic
 * replay engine ever runs against it; the rest are the verifier's own verdicts. Only `unverified` is
 * written today — this is a seam for that later workstream, not yet load-bearing.
 */
export const groundingStatuses = [
  'unverified',
  'holding',
  'patched',
  'resolved',
  'invalidated',
  'needsReview',
] as const;
export type GroundingStatus = (typeof groundingStatuses)[number];
export const groundingStatusSchema = z.enum(groundingStatuses);

/** Whether an event was made by a person or written by the pipeline (a replay verdict, an auto-resolve). */
export const trackedItemActorTypes = ['user', 'system'] as const;
export type TrackedItemActorType = (typeof trackedItemActorTypes)[number];
export const trackedItemActorTypeSchema = z.enum(trackedItemActorTypes);

/**
 * What produced an event. `status_change` is a person's action (or the pipeline's own auto-resolve).
 * The other two are the pipeline's word on a row a person owns, recorded whether or not the status
 * moved — `fromStatus === toStatus` is the normal case: `grounding_verdict` is the evidence-replay
 * verifier's verdict on the captured fact, `writer_retirement` is a run's writer retiring the tracked
 * finding. A verdict on an already `completed`/`dismissed` row is recorded and changes nothing else.
 */
export const trackedItemEventKinds = [
  'status_change',
  'grounding_verdict',
  'writer_retirement',
] as const;
export type TrackedItemEventKind = (typeof trackedItemEventKinds)[number];
export const trackedItemEventKindSchema = z.enum(trackedItemEventKinds);

const groundingValueSchema = z.union([z.number(), z.string(), z.boolean()]).nullable();

/** `metadata` of a `grounding_verdict` event. `newValue` is null when the fact could not be read. */
export const groundingVerdictEventMetadataSchema = z.object({
  runId: z.string(),
  verdict: groundingStatusSchema,
  previousVerdict: groundingStatusSchema,
  oldValue: groundingValueSchema,
  newValue: groundingValueSchema,
  reason: z.string().nullable(),
});
export type GroundingVerdictEventMetadata = z.infer<typeof groundingVerdictEventMetadataSchema>;

/** `metadata` of a `writer_retirement` event. */
export const writerRetirementEventMetadataSchema = z.object({
  runId: z.string(),
  reason: z.enum(retiredReasons),
  note: z.string().nullable(),
});
export type WriterRetirementEventMetadata = z.infer<typeof writerRetirementEventMetadataSchema>;

/** `metadata` of a `status_change` event, recorded whenever the assignee actually changed. */
export const statusChangeEventMetadataSchema = z.object({
  fromAssigneeUserId: z.string().nullable(),
  toAssigneeUserId: z.string().nullable(),
});
export type StatusChangeEventMetadata = z.infer<typeof statusChangeEventMetadataSchema>;

export const trackedItemDTOSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  loopId: z.number().int().positive().nullable(),
  findingId: z.string(),
  /** null = the whole finding is tracked; set = one specific recommendation inside it. */
  recommendationId: z.string().nullable(),
  status: trackedItemStatusSchema,
  assigneeUserId: z.string().nullable(),
  createdByUserId: z.string(),
  lastActorUserId: z.string().nullable(),
  dueAt: z.string().nullable(),
  note: z.string().nullable(),
  /** Frozen at write time — lets the row render even if the source finding is later retired,
   * capped out of a run, or its run becomes unreadable on a future schema-version bump. */
  findingHeadlineSnapshot: z.string(),
  recommendationTitleSnapshot: z.string().nullable(),
  /** Soft reference only, no FK: the run row this came from is allowed to become unreadable or be
   * pruned without taking the tracked item down with it. */
  sourceRunId: z.string(),
  /** The newest completed run whose findings still carried this item (the finding, or the specific
   * recommendation when one is tracked); null until the first run completed after it was tracked.
   * Refreshed by the pipeline, so "not seen since <date>" is readable off the row. */
  lastSeenRunId: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  /** Captured at pin time from the finding's own group; the evidence-replay comparator reads this to
   * decide what counts as "resolved" for this tracked item. */
  findingGroup: z.enum(findingGroups).nullable(),
  /** The evidence-replay engine's captured fact contract, copied by value at pin time. Left untyped
   * here (rather than importing findingGroundingSchema) to avoid a dependency the replay workstream
   * doesn't need yet; that engine parses/validates its own shape on read. */
  groundingSnapshot: z.record(z.string(), z.unknown()).nullable(),
  groundingStatus: groundingStatusSchema,
  currentValue: z.union([z.number(), z.string(), z.boolean()]).nullable(),
  lastVerifiedAt: z.string().nullable(),
  lastVerifiedRunId: z.string().nullable(),
  resolutionNote: z.string().nullable(),
  /** Set on the first replay cycle that finds the underlying fact missing; two consecutive
   * occurrences finalize `groundingStatus: 'invalidated'` rather than one bad read doing so. */
  pendingInvalidationSince: z.string().nullable(),
  ...timestampDTOSchema.shape,
});
export type TrackedItemDTO = z.infer<typeof trackedItemDTOSchema>;

export const trackedItemEventDTOSchema = z.object({
  id: z.string(),
  trackedItemId: z.string(),
  organizationId: z.string(),
  kind: trackedItemEventKindSchema,
  fromStatus: trackedItemStatusSchema.nullable(),
  toStatus: trackedItemStatusSchema,
  actorType: trackedItemActorTypeSchema,
  actorUserId: z.string().nullable(),
  note: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  ...timestampDTOSchema.shape,
});
export type TrackedItemEventDTO = z.infer<typeof trackedItemEventDTOSchema>;

export const trackedItemDetailDTOSchema = trackedItemDTOSchema.extend({
  history: z.array(trackedItemEventDTOSchema),
});
export type TrackedItemDetailDTO = z.infer<typeof trackedItemDetailDTOSchema>;

// `agent_tracked`/`auto_resolved`/`superseded` are deliberately excluded — a caller cannot request
// either directly, matching `humanSettableTrackedItemStatuses`.
const trackedItemStatusFields = {
  status: humanSettableTrackedItemStatusSchema,
  assigneeUserId: z.string().min(1).nullable().optional(),
  note: z.string().trim().min(1).max(1000).optional(),
  dueAt: z.string().datetime().optional(),
};

/**
 * Omitting `assigneeUserId` always means "unchanged". Beyond that: `unassigned` requires an
 * explicit `null`; `assigned` requires a non-null assignee (and, unlike other statuses, has
 * always required the field to be sent at all); `in_motion` rejects an explicit `null` but may
 * omit the field.
 */
const validateAssigneeForStatus = (
  body: { status: HumanSettableTrackedItemStatus; assigneeUserId?: string | null },
  ctx: z.RefinementCtx,
) => {
  const { status, assigneeUserId } = body;
  if (status === 'unassigned' && assigneeUserId !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['assigneeUserId'],
      message: 'assigneeUserId must be null when status is unassigned',
    });
  } else if (status === 'assigned' && assigneeUserId === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['assigneeUserId'],
      message: 'assigneeUserId is required when status is assigned',
    });
  } else if ((status === 'assigned' || status === 'in_motion') && assigneeUserId === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['assigneeUserId'],
      message: 'assigneeUserId cannot be null when status is assigned or in_motion',
    });
  }
};

/** Transitioning an item that already exists, where the run no longer has to be named. */
export const setTrackedItemStatusBodySchema = z
  .object(trackedItemStatusFields)
  .superRefine(validateAssigneeForStatus);
export type SetTrackedItemStatusBody = z.output<typeof setTrackedItemStatusBodySchema>;

// The finding/recommendation is identified by the URL; `loopId` + `runId` name the specific run the
// caller was looking at when it acted, so the first call for a given finding/recommendation can
// validate it exists and snapshot its headline/title server-side rather than trusting client text.
// Ignored on a subsequent call once the tracked item already exists.
export const createOrTransitionTrackedItemBodySchema = z
  .object({
    loopId: z.number().int().positive().nullable(),
    runId: z.string().min(1),
    ...trackedItemStatusFields,
  })
  .superRefine(validateAssigneeForStatus);
export type CreateOrTransitionTrackedItemBody = z.output<
  typeof createOrTransitionTrackedItemBodySchema
>;

/** Not `createPageDTOSchema`: the service answers `nextCursor: null` on the last page, which the
 * shared generic does not allow. */
export const trackedItemsPageDTOSchema = z.object({
  items: z.array(trackedItemDTOSchema),
  nextCursor: z.string().nullable().optional(),
});
export type TrackedItemsPageDTO = z.infer<typeof trackedItemsPageDTOSchema>;

/** The Loop Intelligence tabs that carry a per-user "unseen activity" marker. */
export const trackedItemSeenTabs = ['assigned', 'completed'] as const;
export type TrackedItemSeenTab = (typeof trackedItemSeenTabs)[number];
export const trackedItemSeenTabSchema = z.enum(trackedItemSeenTabs);

export const unseenTrackedItemTabsDTOSchema = z.object({
  assigned: z.boolean(),
  completed: z.boolean(),
});
export type UnseenTrackedItemTabsDTO = z.infer<typeof unseenTrackedItemTabsDTOSchema>;

/** Omitting `loopId` means every loop in the org that has tracked items, for the All loops reading. */
export const markTrackedItemTabSeenBodySchema = z.object({
  loopId: z.number().int().positive().optional(),
  tab: trackedItemSeenTabSchema,
});
export type MarkTrackedItemTabSeenBody = z.infer<typeof markTrackedItemTabSeenBodySchema>;
